import { DETAILS_STEP } from './copy';

/**
 * Turns the errors hash a rejected create returns into something the wizard can
 * point at (PD-4064).
 *
 * The server is the authority on more than the browser can reasonably check: the
 * per-country phone rules are Phonelib's, the email uniqueness check is subject to
 * a race with another admin, and User carries rules the wizard knows nothing
 * about. When it rejects a create it says exactly what was wrong — dropping that
 * on the floor and showing "an unexpected error" is what made a bad phone number
 * look like a server fault.
 *
 * So each attribute Rails may complain about is mapped back to the step that
 * collects it and the field that displays it. Anything unmapped keeps the generic
 * error panel, which is the right home for a genuine surprise.
 */

/** Rails attribute -> the draft field it belongs to, its step, and its label. */
const FIELDS = {
  first_name: { field: 'firstName', step: 2, label: DETAILS_STEP.firstName },
  last_name: { field: 'lastName', step: 2, label: DETAILS_STEP.lastName },
  email: { field: 'email', step: 2, label: DETAILS_STEP.email },
  emails: { field: 'email', step: 2, label: DETAILS_STEP.email },
  job_role: { field: 'jobRole', step: 2, label: DETAILS_STEP.jobRole },
  mobile: { field: 'mobile', step: 2, label: DETAILS_STEP.mobile },
  mobile_country_code: { field: 'mobile', step: 2, label: DETAILS_STEP.mobile },
  sso_provider: {
    field: 'ssoProvider',
    step: 2,
    label: DETAILS_STEP.authentication,
  },
};

/**
 * @param {object} fieldErrors `{ mobile: ['must be valid'] }` as Rails sends it
 * @returns {{messages: object, step: number|null}} messages keyed by draft field,
 *   and the earliest step carrying one — where the wizard should land. `step` is
 *   null when nothing mapped, meaning the caller should show the error panel.
 */
export const toFieldMessages = (fieldErrors = {}) => {
  const mapped = Object.entries(fieldErrors)
    .map(([attribute, errors]) => [FIELDS[attribute], errors])
    .filter(([known]) => Boolean(known));

  const messages = {};

  mapped.forEach(([known, errors]) => {
    // First complaint about a field wins: there is room for one line, and two
    // attributes can share a field (mobile and mobile_country_code both do).
    if (messages[known.field]) return;

    // Rails messages are fragments ("must be valid"), so they need their subject.
    messages[known.field] = `${known.label} ${[].concat(errors)[0]}`;
  });

  const steps = mapped.map(([known]) => known.step);

  return { messages, step: steps.length ? Math.min(...steps) : null };
};

export default toFieldMessages;
